import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-google-charts";

import Select from "components/Input/Select";
import LoadingDots from "components/LoadingDots";

import classes from "./style.module.scss";

const MONTHS = [
    { key: "january", short: "Jan", label: "January" },
    { key: "february", short: "Feb", label: "February" },
    { key: "march", short: "Mar", label: "March" },
    { key: "april", short: "Apr", label: "April" },
    { key: "may", short: "May", label: "May" },
    { key: "june", short: "Jun", label: "June" },
    { key: "july", short: "Jul", label: "July" },
    { key: "august", short: "Aug", label: "August" },
    { key: "september", short: "Sep", label: "September" },
    { key: "october", short: "Oct", label: "October" },
    { key: "november", short: "Nov", label: "November" },
    { key: "december", short: "Dec", label: "December" },
];

// Paths follow AllResults/Results/Monthly in the HOT2000 file. Energy quantities that
// sum to the annual GJ figures are GJ; the larger monthly load and heat-loss figures are MJ.
const SERIES = [
    {
        id: "fractionHeatingOff",
        group: "Heating",
        label: "Fraction of Time Heating System Not Operating",
        unit: "",
        decimals: 3,
        path: ["FractionOfTimeHeatingSystemNotOperating"],
    },
    {
        id: "solarHotWater",
        group: "Solar Hot Water",
        label: "Energy Contribution",
        unit: "GJ",
        decimals: 2,
        total: "sum",
        path: ["SolarHotWaterEnergyContribution"],
    },
    {
        id: "electricalGross",
        group: "Electrical Consumption",
        label: "Gross",
        unit: "GJ",
        decimals: 2,
        total: "sum",
        path: ["ElectricalConsumption", "Gross"],
    },
    {
        id: "electricalIms",
        group: "Electrical Consumption",
        label: "IMS or P.9 Heating Hours",
        unit: "GJ",
        decimals: 2,
        total: "sum",
        path: ["ElectricalConsumption", "ImsOrP9HeatingHours"],
    },
    {
        id: "gainsInternal",
        group: "Gains",
        label: "Utilized Internal",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["Gains", "UtilizedInternal"],
    },
    {
        id: "gainsSolar",
        group: "Gains",
        label: "Utilized Solar",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["Gains", "UtilizedSolar"],
    },
    {
        id: "gainsCrawlspace",
        group: "Gains",
        label: "Crawlspace Solar",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["Gains", "CrawlspaceSolar"],
    },
    {
        id: "auxiliaryHeat",
        group: "Auxiliary Heat",
        label: "Utilized Auxiliary Heat Required",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["UtilizedAuxiliaryHeatRequired"],
    },
    {
        id: "tempCrawlspace",
        group: "Temperatures",
        label: "Crawl Space",
        unit: "°C",
        decimals: 1,
        path: ["Temperatures", "CrawlSpace"],
    },
    {
        id: "tempAttic",
        group: "Temperatures",
        label: "Attic",
        unit: "°C",
        decimals: 1,
        path: ["Temperatures", "Attic"],
    },
    {
        id: "pvUtilized",
        group: "Load",
        label: "Photovoltaic Utilized",
        unit: "GJ",
        decimals: 2,
        total: "sum",
        path: ["Load", "PhotoVoltaicUtilized"],
    },
    {
        id: "pvAvailable",
        group: "Load",
        label: "Photovoltaic Available",
        unit: "GJ",
        decimals: 2,
        total: "sum",
        path: ["Load", "PhotoVoltaicAvailable"],
    },
    {
        id: "windUtilized",
        group: "Load",
        label: "Wind Utilized",
        unit: "GJ",
        decimals: 2,
        total: "sum",
        path: ["Load", "WindUtilized"],
    },
    {
        id: "windAvailable",
        group: "Load",
        label: "Wind Available",
        unit: "GJ",
        decimals: 2,
        total: "sum",
        path: ["Load", "WindAvailable"],
    },
    {
        id: "grossThermal",
        group: "Load",
        label: "Gross Thermal",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["Load", "GrossThermal"],
    },
    {
        id: "basementHeating",
        group: "Load",
        label: "Basement Heating",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["Load", "Basement", "Heating"],
    },
    {
        id: "basementCooling",
        group: "Load",
        label: "Basement Cooling",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["Load", "Basement", "Cooling"],
    },
    {
        id: "heatLossCeiling",
        group: "Heat Loss",
        label: "Ceiling",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["HeatLoss", "Ceiling"],
    },
    {
        id: "heatLossWalls",
        group: "Heat Loss",
        label: "Main Walls",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["HeatLoss", "MainWalls"],
    },
    {
        id: "heatLossDoors",
        group: "Heat Loss",
        label: "Doors",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["HeatLoss", "Doors"],
    },
    {
        id: "heatLossFloors",
        group: "Heat Loss",
        label: "Exposed Floors",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["HeatLoss", "ExposedFloors"],
    },
    {
        id: "heatLossCrawlspace",
        group: "Heat Loss",
        label: "Crawlspace",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["HeatLoss", "Crawlspace"],
    },
    {
        id: "heatLossSlab",
        group: "Heat Loss",
        label: "Slab",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["HeatLoss", "Slab"],
    },
    {
        id: "heatLossBasementAbove",
        group: "Heat Loss",
        label: "Basement Above Grade",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["HeatLoss", "Basement", "AboveGrade"],
    },
    {
        id: "heatLossBasementBelow",
        group: "Heat Loss",
        label: "Basement Below Grade",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["HeatLoss", "Basement", "BelowGrade"],
    },
    {
        id: "heatLossAboveGradeWall",
        group: "Heat Loss",
        label: "Basement Above Grade Wall",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["HeatLoss", "Basement", "AboveGradeWall"],
    },
    {
        id: "heatLossFloorHeaders",
        group: "Heat Loss",
        label: "Basement Floor Headers",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["HeatLoss", "Basement", "FloorHeaders"],
    },
    {
        id: "heatLossPonyWall",
        group: "Heat Loss",
        label: "Basement Pony Wall",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["HeatLoss", "Basement", "PonyWall"],
    },
    {
        id: "heatLossFloorsAbove",
        group: "Heat Loss",
        label: "Floors Above Basement",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["HeatLoss", "Basement", "FloorsAbove"],
    },
    {
        id: "heatLossAirLeakage",
        group: "Heat Loss",
        label: "Air Leakage and Mechanical Ventilation",
        unit: "MJ",
        decimals: 1,
        total: "sum",
        path: ["HeatLoss", "Basement", "AirLeakageAndMechanicalVentilation"],
    },
    {
        id: "mainSolarUtil",
        group: "Factors — Main Floors",
        label: "Solar Utilization",
        unit: "",
        decimals: 3,
        path: ["Factors", "MainFloors", "SolarUtilization"],
    },
    {
        id: "mainGainLoad",
        group: "Factors — Main Floors",
        label: "Gain/Load Ratio",
        unit: "",
        decimals: 2,
        path: ["Factors", "MainFloors", "GainLoadRatio"],
    },
    {
        id: "mainMassGain",
        group: "Factors — Main Floors",
        label: "Mass/Gain Ratio",
        unit: "",
        decimals: 2,
        path: ["Factors", "MainFloors", "MassGainRatio"],
    },
    {
        id: "mainInternalUtil",
        group: "Factors — Main Floors",
        label: "Internal Gains Utilization",
        unit: "",
        decimals: 2,
        path: ["Factors", "MainFloors", "InternalGainsUtilization"],
    },
    {
        id: "basementSolarUtil",
        group: "Factors — Basement",
        label: "Solar Utilization",
        unit: "",
        decimals: 3,
        path: ["Factors", "Basement", "SolarUtilization"],
    },
    {
        id: "basementGainLoad",
        group: "Factors — Basement",
        label: "Gain/Load Ratio",
        unit: "",
        decimals: 2,
        path: ["Factors", "Basement", "GainLoadRatio"],
    },
    {
        id: "basementMassGain",
        group: "Factors — Basement",
        label: "Mass/Gain Ratio",
        unit: "",
        decimals: 2,
        path: ["Factors", "Basement", "MassGainRatio"],
    },
    {
        id: "achNatural",
        group: "Air Change Rate",
        label: "Natural",
        unit: "ACH",
        decimals: 3,
        path: ["AirChangeRate", "Natural"],
    },
    {
        id: "achTotal",
        group: "Air Change Rate",
        label: "Total",
        unit: "ACH",
        decimals: 3,
        path: ["AirChangeRate", "Total"],
    },
];

const readMonthValues = (monthly, path) => {
    const node = path.reduce((current, key) => current?.[key], monthly);
    const attributes = node?._attributes;
    if (!attributes) return null;

    const byMonth = Object.entries(attributes).reduce((values, [key, value]) => {
        values[key.toLowerCase()] = value;
        return values;
    }, {});

    if (!MONTHS.some(({ key }) => byMonth[key] != null && byMonth[key] !== "")) return null;

    return MONTHS.map(({ key }) => {
        const raw = byMonth[key];
        if (raw == null || raw === "") return null;
        const value = Number(raw);
        return Number.isFinite(value) ? value : null;
    });
};

const formatValue = (value, decimals) => {
    if (value == null) return "—";
    return value.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
};

const H2kMonthlyResults = ({ monthly }) => {
    const available = useMemo(
        () =>
            SERIES.map((series) => ({ ...series, values: readMonthValues(monthly, series.path) })).filter(
                (series) => series.values
            ),
        [monthly]
    );

    const preferredId = available.find((series) => series.id === "auxiliaryHeat")?.id || available[0]?.id || "";
    const [selectedId, setSelectedId] = useState(preferredId);

    useEffect(() => {
        if (!available.some((series) => series.id === selectedId)) {
            setSelectedId(preferredId);
        }
    }, [available, preferredId, selectedId]);

    const selected = available.find((series) => series.id === selectedId) || available[0];

    if (!selected) {
        return (
            <section className={classes.monthlyResults}>
                <h3>Monthly Results</h3>
                <p className={classes.empty}>No monthly HOT2000 results are available for this case.</p>
            </section>
        );
    }

    const title = `${selected.group}: ${selected.label}`;
    const unitLabel = selected.unit ? ` (${selected.unit})` : "";
    const total = selected.total === "sum" ? selected.values.reduce((sum, value) => sum + (value || 0), 0) : null;

    const chartData = [
        ["Month", selected.label],
        ...MONTHS.map((month, index) => [month.short, selected.values[index]]),
    ];

    return (
        <section className={classes.monthlyResults}>
            <div className={classes.header}>
                <h3>Monthly Results</h3>
                <div className={classes.select}>
                    <Select
                        name="h2kMonthlyResult"
                        label="Result"
                        search
                        searchPlaceholder="Search monthly results"
                        options={available.map((series) => ({
                            value: series.id,
                            label: `${series.group}: ${series.label}`,
                        }))}
                        input={{
                            value: selected.id,
                            onChange: setSelectedId,
                        }}
                    />
                </div>
            </div>
            <Chart
                width="100%"
                height="320px"
                chartType="ColumnChart"
                loader={<LoadingDots />}
                data={chartData}
                options={{
                    legend: { position: "none" },
                    colors: ["#0049c6"],
                    bar: { groupWidth: "62%" },
                    chartArea: { left: 72, top: 16, width: "90%", height: "72%" },
                    hAxis: {
                        textStyle: { fontName: "Noto Sans TC", fontSize: 12, color: "#646f81" },
                    },
                    vAxis: {
                        title: selected.unit || undefined,
                        textStyle: { fontName: "Noto Sans TC", fontSize: 12, color: "#646f81" },
                        titleTextStyle: { fontName: "Noto Sans TC", fontSize: 12, color: "#646f81", italic: false },
                    },
                    tooltip: {
                        textStyle: { fontName: "Noto Sans TC", fontSize: 12 },
                    },
                }}
            />
            <table className={classes.table}>
                <caption>{`${title}${unitLabel}`}</caption>
                <thead>
                    <tr>
                        <th scope="col">Month</th>
                        <th scope="col">{selected.unit ? `Value (${selected.unit})` : "Value"}</th>
                    </tr>
                </thead>
                <tbody>
                    {MONTHS.map((month, index) => (
                        <tr key={month.key}>
                            <th scope="row">{month.label}</th>
                            <td>{formatValue(selected.values[index], selected.decimals)}</td>
                        </tr>
                    ))}
                </tbody>
                {total != null && (
                    <tfoot>
                        <tr>
                            <th scope="row">Total</th>
                            <td>{formatValue(total, selected.decimals)}</td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </section>
    );
};

export default H2kMonthlyResults;
